import { Form, Input, Select, Space, Typography } from "antd";
import { useState } from "react";
import { useProject, useProjects } from "@/common/artemis-operations/projects";
import { useCreateWebhook } from "@/common/artemis-operations/webhooks";
import { WebhookAction } from "@/common/model";
import { useNotify } from "@/common/use-notify";
import { AgentSelector } from "@/components/agents/agent-selector";
import { SegmentedSelection } from "@/components/segmented-selection";
import { Wizard } from "@/components/wizard";

interface WebhookCreateWizardProps {
	open: boolean;
	workspaceId: string;
	onClose: () => void;
	/** Called with the new webhook and its secret, which the caller must show once. */
	onCreated: (issued: { webhookId: string; secret: string }) => void;
}

/**
 * Defines a webhook (ARCH-035): what it does, the agent and instructions for created tasks, and an
 * optional default project/deployment for callers that name neither.
 */
export function WebhookCreateWizard({
	open,
	workspaceId,
	onClose,
	onCreated,
}: WebhookCreateWizardProps) {
	const [form] = Form.useForm();
	const [action, setAction] = useState<WebhookAction>(
		WebhookAction.CREATE_TASK,
	);
	const { mutate: createWebhook, isPending } = useCreateWebhook();
	const { data: projects } = useProjects(workspaceId);
	const notify = useNotify();

	const projectId = Form.useWatch("projectId", form) as string | undefined;
	const { data: project } = useProject(projectId ?? "");

	const reset = () => {
		setAction(WebhookAction.CREATE_TASK);
		form.resetFields();
	};

	const handleComplete = () => {
		form.validateFields().then((vals) => {
			createWebhook(
				{
					workspaceId,
					name: vals.name,
					description: vals.description ?? "",
					action,
					agentId:
						action === WebhookAction.CREATE_TASK ? vals.agentId : undefined,
					instructions:
						action === WebhookAction.CREATE_TASK
							? (vals.instructions ?? undefined)
							: undefined,
					projectId: vals.projectId ?? undefined,
					deploymentId: vals.deploymentId ?? undefined,
				},
				{
					onSuccess: (data) => {
						onCreated({
							webhookId: data.createWebhook.webhook.id,
							secret: data.createWebhook.secret,
						});
						reset();
						onClose();
					},
					onError: (err) =>
						notify.error(
							(err as Error).message || "Failed to create the webhook.",
						),
				},
			);
		});
	};

	const isTask = action === WebhookAction.CREATE_TASK;

	return (
		<Wizard
			open={open}
			title="Create Webhook"
			completeLabel="Create"
			onComplete={handleComplete}
			form={form}
			loading={isPending}
			size="large"
			onCancel={() => {
				onClose();
				reset();
			}}
			pages={[
				{
					id: "details",
					title: "Details",
					content: (
						<Space vertical size="small" style={{ width: "100%" }}>
							<Form.Item
								name="name"
								label="Name"
								rules={[{ required: true, min: 3, max: 500 }]}
								extra={
									isTask
										? "Also the default name of the tasks it creates; a caller can send its own."
										: undefined
								}
							>
								<Input maxLength={500} placeholder="e.g. Monitoring alerts" />
							</Form.Item>
							<Form.Item label="What it does">
								<SegmentedSelection
									value={action}
									onChange={(v) => {
										setAction(v as WebhookAction);
										form.setFieldsValue({
											agentId: undefined,
											instructions: undefined,
										});
									}}
									options={[
										{
											value: WebhookAction.CREATE_TASK,
											label: "Create & start a task",
											description:
												"Each call creates a task and dispatches it to an agent. The caller picks the project, deployment, agent and task text per call.",
										},
										{
											value: WebhookAction.DISCOVER_DEPLOYMENT,
											label: "Discover a deployment",
											description:
												"Each call rescans the deployment the caller names.",
										},
									]}
								/>
							</Form.Item>
							{isTask && (
								<Form.Item
									name="description"
									label="Default task description"
									rules={[{ max: 5000 }]}
									extra="Used when a call sends no task.description."
								>
									<Input.TextArea
										rows={3}
										maxLength={5000}
										showCount
										placeholder="e.g. Investigate the alarm in the payload and report the likely cause."
									/>
								</Form.Item>
							)}
						</Space>
					),
				},
				...(isTask
					? [
							{
								id: "agent",
								title: "Agent",
								content: (
									<Space vertical size="small" style={{ width: "100%" }}>
										<Typography.Paragraph>
											Defaults for calls that name no agentId and send no
											task.instructions. One webhook can serve many callers,
											each choosing its own.
										</Typography.Paragraph>
										<AgentSelector
											workspaceId={workspaceId}
											fieldName="agentId"
											onAgentChange={(id) => form.setFieldValue("agentId", id)}
										/>
										<Form.Item
											name="instructions"
											label="Default agent instructions"
											extra="Replaced by a call's task.instructions when it sends them."
										>
											<Input.TextArea rows={8} />
										</Form.Item>
									</Space>
								),
							},
						]
					: []),
				{
					id: "target",
					title: "Default target",
					content: (
						<Space vertical size="small" style={{ width: "100%" }}>
							<Typography.Paragraph>
								Callers name a projectId or deploymentId in the URL; it must be
								in this workspace. Set a default for calls that name neither.
							</Typography.Paragraph>
							<Form.Item name="projectId" label="Project">
								<Select
									allowClear
									placeholder="No default project"
									options={(projects ?? [])
										.filter((p) => p != null)
										.map((p) => ({ value: p.id, label: p.name }))}
									onChange={() => form.setFieldValue("deploymentId", undefined)}
								/>
							</Form.Item>
							<Form.Item
								name="deploymentId"
								label="Deployment"
								extra={projectId ? undefined : "Select a project first."}
							>
								<Select
									allowClear
									disabled={!projectId}
									placeholder="No default deployment"
									options={(project?.deployments ?? []).map((d) => ({
										value: d.id,
										label: d.name,
									}))}
								/>
							</Form.Item>
						</Space>
					),
				},
			]}
		/>
	);
}
